import { Box, Flex } from '@fundamentals'
import { Alert, Divider, Stack, TextInput } from '@common/material'
import { localizedStrings } from '@core/strings'
import { RegisterItemPropertyFormItem } from './RegisterItemPropertyFormItem'
import { RegisterPropertyFormulaOverrideActions } from './properties/RegisterPropertyFormulaOverrideActions'
import { NavigationAwareTabs } from '@elements'
import { Controller, useFormContext } from 'react-hook-form'
import { Register } from '@core/entities/register/Register'
import {
  RegisterItem,
  RegisterItemFlatPropertyData,
} from '@core/entities/register/RegisterItem'
import { RegisterTemplate } from '@core/entities/register/RegisterTemplate'
import { RegisterTemplateProperty } from '@core/entities/register/RegisterTemplateProperty'

type RegisterItemPropertiesFormTabsProps = {
  register: Register
  registerItem: RegisterItem
  registerTemplate: RegisterTemplate
  registerProperties: RegisterTemplateProperty[]
  diagramView?: boolean
  focusProperty?: RegisterItemFlatPropertyData
  propertyId?: number
  showReasonForUpdate?: boolean
}

export const RegisterItemPropertiesFormTabs: React.FC<
  RegisterItemPropertiesFormTabsProps
> = ({
  register,
  registerItem,
  registerTemplate,
  registerProperties,
  diagramView = false,
  focusProperty,
  propertyId,
  showReasonForUpdate = true,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext() // retrieve all hook methods

  const registerPropertiesWithoutFormulaInView = registerProperties
    .filter((property) => !property?.formulaConfig)
    .filter(
      (property) =>
        !focusProperty ||
        property.id === parseInt(focusProperty.registerPropertyId),
    )
    .filter((property) => (propertyId ? property.id === propertyId : property))

  const registerPropertiesWithFormulaInView = registerProperties
    .filter((property) => !!property?.formulaConfig)
    .filter(
      (property) =>
        !focusProperty ||
        property.id === parseInt(focusProperty.registerPropertyId),
    )
    .filter((property) => (propertyId ? property.id === propertyId : property))

  const registerPropertiesToCalculate =
    registerProperties
      .filter((property) => property?.formulaConfig || property?.prefix)
      .filter((property) =>
        propertyId ? property.id === propertyId : property,
      ) ?? []

  const editNonFormulaProperties = (
    <Flex flexDirection='column' my={2}>
      {(registerItem ||
        (!registerTemplate?.uniqueIdSystemControlled &&
          registerTemplate?.uniqueIdConfig?.type !== 'INCREMENTAL')) &&
        !propertyId && (
          <Controller
            name='uniqueId'
            render={({ field: { value, onChange } }) => (
              <TextInput
                key='uniqueId'
                disabled={!!registerItem}
                label={localizedStrings.uniqueId}
                placeholder={localizedStrings.typeHere}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
                value={value}
                onChange={onChange}
              />
            )}
          />
        )}
      {registerPropertiesWithoutFormulaInView.map((column) => {
        return (
          <RegisterItemPropertyFormItem
            key={column.id}
            property={column}
            register={register}
            registerItem={registerItem}
            registerTemplate={registerTemplate}
          />
        )
      })}
    </Flex>
  )

  const editFormulaProperties = (
    <Box sx={{ mt: 2, mb: 2 }}>
      {registerPropertiesWithFormulaInView.map((column) => {
        return (
          <Flex key={column.id} width={'100%'} alignItems={'center'}>
            <RegisterItemPropertyFormItem
              key={column.id}
              // For now we don't allow editing formula properties, in the future we will allow overriding formula properties
              disabled
              property={column}
              register={register}
              registerItem={registerItem}
              registerTemplate={registerTemplate}
            />
            {column.canBeOverridden && (
              <RegisterPropertyFormulaOverrideActions
                property={column}
                register={register}
                registerItem={registerItem}
                registerTemplate={registerTemplate}
              />
            )}
          </Flex>
        )
      })}
    </Box>
  )

  const tabs = []

  if (registerPropertiesWithoutFormulaInView?.length) {
    tabs.push({
      name: localizedStrings.properties,
      label: localizedStrings.properties,
      component: editNonFormulaProperties,
    })
  }

  if (registerItem?.id && registerPropertiesWithFormulaInView?.length) {
    tabs.push({
      name: localizedStrings.calculatedByFormula,
      label: localizedStrings.calculatedByFormula,
      component: editFormulaProperties,
    })
  }

  return (
    <Flex flexDirection='column' mt={2}>
      {tabs?.length > 1 ? (
        <NavigationAwareTabs tabs={tabs} withoutQueryParam />
      ) : (
        <Flex flexDirection='column'>
          {!!registerPropertiesWithoutFormulaInView?.length &&
            editNonFormulaProperties}
          {!!registerItem?.id &&
            !!registerPropertiesWithFormulaInView?.length &&
            editFormulaProperties}
        </Flex>
      )}
      <Stack spacing={2}>
        {!!Object.keys(errors).length && (
          <ErrorMessage className='mb-0'>
            {localizedStrings.pleaseFillAllFieldsCorrrectly}
          </ErrorMessage>
        )}

        {!!registerPropertiesToCalculate.length && (
          <Alert severity='info'>
            {propertyId
              ? localizedStrings.thisPropertyIsCalculatedAutomatically
              : `${
                  localizedStrings.theFollowingPropertiesWillBeCalculated
                }: ${registerPropertiesToCalculate
                  .map((property) => property.name)
                  .join(', ')}`}
          </Alert>
        )}
        {!!register?.diagrams?.length && !diagramView && (
          <Alert severity='info'>
            {localizedStrings.toEditTheAnnotationsOfThisItemSwitchToDiagramView}
          </Alert>
        )}
      </Stack>

      <Divider sx={{ mb: showReasonForUpdate ? 4 : 0 }} />
      {showReasonForUpdate && (
        <Controller
          control={control}
          name='updateNote'
          render={({ field: { value, onChange }, formState: { errors } }) => (
            <TextInput
              label={localizedStrings.reasonForUpdate}
              data-test='reason-for-update'
              placeholder={localizedStrings.typeHere}
              InputLabelProps={{ shrink: true }}
              multiline
              rows={4}
              error={
                !!Object.keys(errors).includes(localizedStrings.reasonForUpdate)
              }
              value={value}
              onChange={onChange}
            />
          )}
        />
      )}
    </Flex>
  )
}
